import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, PROFILE_SAFE_COLUMNS, type Database } from "@/lib/supabase";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import { identifyUser, resetAnalytics, ageToBucket, track, analytics, type Persona } from "@/lib/analytics";
import { identifyUserForBilling } from "@/lib/googlePlayBilling";
import { computeAge } from "@/lib/age";
import { isWebPlatform } from "@/lib/platform";

export type Profile = Database["public"]["Tables"]["profiles"]["Row"];
export type Academy = Database["public"]["Tables"]["academies"]["Row"];

/** sessionStorage key for the per-tab "user has actively signed in"
 *  flag. On WEB the Supabase session lives in localStorage and survives
 *  tab close, but we treat tab close as a sign-out signal — the operator
 *  must re-authenticate (Google click or password submit) before any
 *  protected route renders. sessionStorage is the right tool: scoped
 *  per tab, cleared automatically when the tab closes. */
const SESSION_AUTHED_KEY = "sportsbio_session_active";

/** Whether closing the app/tab should force a re-authentication.
 *
 *  True on web (a browser can be a shared machine), false on native. On
 *  a device the session must outlive the process: killing the app from
 *  the recents list is not a sign-out, and treating it as one was what
 *  sent returning users to the login screen. */
const REQUIRE_REAUTH_ON_FRESH_LAUNCH = isWebPlatform();

function readSessionAuthedFlag(): boolean {
  try { return sessionStorage.getItem(SESSION_AUTHED_KEY) === "1"; } catch { return false; }
}
function writeSessionAuthedFlag(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(SESSION_AUTHED_KEY, "1");
    else sessionStorage.removeItem(SESSION_AUTHED_KEY);
  } catch { /* private mode / quota — accept silent fail */ }
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  /** Populated only when profile.role === 'academy'. Otherwise null. */
  academy: Academy | null;
  /** True only during the initial session probe on mount. Never gets stuck on. */
  loading: boolean;
  /** Has the user actively signed in during THIS tab session? Persisted
   *  Supabase sessions in localStorage survive tab close; this flag
   *  does not. SplashScreen + RequireAuth + AuthScreen all gate on
   *  this so closing and reopening the tab forces a fresh sign-in
   *  even though a valid Supabase session is still in storage. */
  sessionAuthenticated: boolean;
  refreshProfile: () => Promise<void>;
  refreshAcademy: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [academy, setAcademy] = useState<Academy | null>(null);
  const [loading, setLoading] = useState(true);
  // WEB ONLY. Initialised from sessionStorage so an in-tab navigation (or
  // hot reload) preserves the flag, but a closed-then-reopened tab starts
  // fresh and forces re-auth — the browser may be a shared computer.
  // Updated by the onAuthStateChange listener below on SIGNED_IN /
  // SIGNED_OUT.
  const [sessionAuthenticatedState, setSessionAuthenticatedState] = useState<boolean>(
    () => readSessionAuthedFlag(),
  );
  // On NATIVE this gate is simply off. sessionStorage dies with the
  // WebView, so force-killing the app wiped the flag, and a returning
  // user with a perfectly valid persisted session was bounced to /auth by
  // RequireAuth and SplashScreen — the "app logs me out when I kill it"
  // bug. A phone is not a shared computer: possession of an unlocked
  // device is the re-auth story, and users stay signed in until they
  // actually log out.
  const sessionAuthenticated = REQUIRE_REAUTH_ON_FRESH_LAUNCH
    ? sessionAuthenticatedState
    : true;
  const setSessionAuthenticated = useCallback((value: boolean) => {
    writeSessionAuthedFlag(value);
    setSessionAuthenticatedState(value);
  }, []);
  // Track the latest user id we loaded a profile for, so rapid auth events
  // don't leave us showing a stale profile.
  const loadedForUser = useRef<string | null>(null);
  // Latched flag — fired once per session when email_verified_at flips from
  // null to set so the email_verified analytics event isn't double-counted.
  const verifiedTracked = useRef(false);
  // Previous email_verified_at, used inside loadProfile (which can't read
  // `profile` state directly without re-creating the callback every render).
  const prevEmailVerifiedAt = useRef<string | null>(null);

  const loadAcademy = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from("academies")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();
    // Drop stale fetches if the user changed underneath us.
    if (loadedForUser.current !== userId) return;
    if (error) {
      // eslint-disable-next-line no-console
      console.error("[auth] failed to load academy", error);
      setAcademy(null);
      return;
    }
    setAcademy((data as Academy) ?? null);
  }, []);

  const loadProfile = useCallback(async (userId: string) => {
    loadedForUser.current = userId;
    // Two reads, because the restricted columns (email / date_of_birth /
    // parent_*) are no longer SELECTable on the profiles table itself —
    // see PROFILE_SAFE_COLUMNS. profile_contacts returns the owner's own
    // row via its is_self branch, so merging the two reproduces the shape
    // callers had when this was a select("*"). parental_consent_token is
    // gone from both and is no longer available anywhere client-side.
    const [{ data, error }, { data: contact }] = await Promise.all([
      supabase
        .from("profiles")
        .select(PROFILE_SAFE_COLUMNS)
        .eq("id", userId)
        .maybeSingle(),
      supabase
        .from("profile_contacts")
        .select("id, email, phone, date_of_birth, parent_email, parent_name")
        .eq("id", userId)
        .maybeSingle(),
    ]);
    // If a newer auth event superseded us, drop the result.
    if (loadedForUser.current !== userId) return;
    if (error) {
      // eslint-disable-next-line no-console
      console.error("[auth] failed to load profile", error);
      setProfile(null);
      setAcademy(null);
      return;
    }
    const base = data as Partial<Profile> | null;
    const next = base ? ({ ...base, ...(contact ?? {}) } as Profile) : null;
    // Detect first-time email verification within a session — the DB trigger
    // sets email_verified_at; we just watch for the flip from null → set.
    if (
      next &&
      prevEmailVerifiedAt.current == null &&
      next.email_verified_at != null &&
      !verifiedTracked.current
    ) {
      verifiedTracked.current = true;
      track({ name: "email_verified" });
    }
    prevEmailVerifiedAt.current = next?.email_verified_at ?? null;
    setProfile(next);
    if (next?.role === "academy") {
      await loadAcademy(userId);
    } else {
      setAcademy(null);
    }
    // Telemetry identity — wires Sentry "user" tag and PostHog person profile.
    // Only persona / age_bucket / signup_date go on the wire — never name or email.
    if (next) {
      const persona: Persona | null =
        next.role === "player" || next.role === "scout" || next.role === "academy"
          ? next.role
          : null;
      setSentryUser({ id: userId, persona });
      if (persona) {
        identifyUser({
          userId,
          persona,
          age_bucket: ageToBucket(computeAge(next.date_of_birth)),
          signup_date: next.created_at?.slice(0, 10),
        });
        // GA4 (web) + Firebase (native) user identification.
        analytics.setUser(userId, persona);
      }
    }
  }, [loadAcademy]);

  // Read the uid from the supabase client directly rather than the React
  // `session` state — the client's internal session is updated synchronously
  // when auth events fire, while React state takes a render to propagate.
  // Without this, calling refreshProfile() right after signUp can no-op
  // because the closure still has the pre-signup session.
  const refreshProfile = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const uid = data.session?.user?.id;
    if (!uid) return;
    await loadProfile(uid);
  }, [loadProfile]);

  const refreshAcademy = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const uid = data.session?.user?.id;
    if (!uid) return;
    await loadAcademy(uid);
  }, [loadAcademy]);

  useEffect(() => {
    let cancelled = false;

    // 1) Initial probe: do we already have a session from a prior visit?
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        setSession(data.session);
        if (data.session?.user) {
          await loadProfile(data.session.user.id);
          // Attribute Play Billing purchases to this account (native only).
          void identifyUserForBilling(data.session.user.id);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[auth] getSession failed", err);
      } finally {
        // Always clear the initial-load flag so the UI can never stay stuck.
        if (!cancelled) setLoading(false);
      }
    })();

    // 2) Ongoing: every sign-in / sign-out / token refresh flows through here.
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (newSession?.user) {
        // fire-and-forget; consumers read `profile` as it fills in.
        void loadProfile(newSession.user.id);
        // Only SIGNED_IN means the operator actively authenticated in
        // this tab (Google OAuth callback or password submit). Don't
        // flip the flag on INITIAL_SESSION (which fires when Supabase
        // restores a persisted session on tab open — that's exactly
        // the case we WANT to gate behind re-auth) or on
        // TOKEN_REFRESHED (silent refresh while the user is using the
        // app — flag is already set).
        if (event === "SIGNED_IN") {
          setSessionAuthenticated(true);
          // Link RevenueCat purchases to the freshly-authenticated user.
          void identifyUserForBilling(newSession.user.id);
        }
      } else {
        loadedForUser.current = null;
        setProfile(null);
        setAcademy(null);
        if (event === "SIGNED_OUT") setSessionAuthenticated(false);
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    loadedForUser.current = null;
    setProfile(null);
    setAcademy(null);
    clearSentryUser();
    resetAnalytics();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      session,
      profile,
      academy,
      loading,
      sessionAuthenticated,
      refreshProfile,
      refreshAcademy,
      signOut,
    }),
    [session, profile, academy, loading, sessionAuthenticated, refreshProfile, refreshAcademy, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
